/**
 * 漁場情報登録フォームの検証（API の zod・L-38 と同じ制約を先に確認する）
 */
import { z } from 'zod';
import type { FishingGroundDetail, FishingGroundEntry, FishingGroundInput, FishSpeciesDto } from './api';

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日付を入力してください');
const optDate = z.union([date, z.literal(''), z.null()]);
const optText = (max: number) => z.string().max(max, `${max}文字以内で入力してください`).nullable();
const optInt = (min: number, max: number, label = '') =>
  z
    .number({ invalid_type_error: '数値で入力してください' })
    .int('整数で入力してください')
    .min(min, `${label}${min}以上で入力してください`)
    .max(max, `${label}${max}以下で入力してください`)
    .nullable();
const optNum = (max: number) => z.number({ invalid_type_error: '数値で入力してください' }).max(max).nullable();

export const entrySchema = z.object({
  surveyDate: date,
  landingPort: optText(50),
  zoneNo: optInt(1, 9999),
  latDeg: optInt(0, 90),
  latMin: optInt(0, 59),
  latSec: optInt(0, 9999),
  lonDeg: optInt(0, 180),
  lonMin: optInt(0, 59),
  lonSec: optInt(0, 9999),
  operationCount: optInt(0, 9999),
  weather: optText(20),
  windDirection: optText(20),
  windForce: optText(20),
  waterTemp: optNum(999.9),
  depth: optNum(9_999_999),
  current: optText(30),
  sonarReaction: optNum(9_999_999),
  schoolCondition: optText(100),
  fishSpeciesId: z.number().int().positive().nullable(),
  standardName: optText(100),
  localName: optText(100),
  count: optInt(0, 2_147_483_647),
  weight: optNum(99_999_999),
  note: optText(2000),
});

export const fishingGroundFormSchema = z
  .object({
    fiscalYear: z.number().int(),
    businessOperatorId: z.number({ invalid_type_error: '事業実施者を選択してください' }).int().nullable(),
    vesselId: z.number({ invalid_type_error: '漁船を選択してください', required_error: '漁船を選択してください' }).int().positive().nullable(),
    ownerName: optText(100),
    fishingMethod: optText(30),
    gear: optText(100),
    gearCount: optInt(0, 999_999),
    voyageFrom: date,
    voyageTo: date,
    reportSubmittedOn: optDate,
    reporterName: optText(100),
    landingDate: optDate,
    fishingGround: optText(100),
    landingTotalInclTax: optInt(0, 2_147_483_647),
    shippingDeductionInclTax: optInt(0, 2_147_483_647),
    netAmountExclTax: optInt(-2_147_483_647, 2_147_483_647),
    entries: z.array(entrySchema).max(500),
  })
  .superRefine((v, ctx) => {
    if (v.businessOperatorId == null) ctx.addIssue({ code: 'custom', path: ['businessOperatorId'], message: '事業実施者名が入力されていません！' });
    if (v.vesselId == null) ctx.addIssue({ code: 'custom', path: ['vesselId'], message: '漁船登録番号が入力されていません！' });
    if (v.voyageFrom && v.voyageTo && v.voyageFrom > v.voyageTo) {
      ctx.addIssue({ code: 'custom', path: ['voyageTo'], message: '調査航海期間の開始日は終了日以前の日付を入力してください' });
    }
    v.entries.forEach((e, i) => {
      if (e.surveyDate && v.voyageFrom && v.voyageTo && (e.surveyDate < v.voyageFrom || e.surveyDate > v.voyageTo)) {
        ctx.addIssue({ code: 'custom', path: ['entries', i, 'surveyDate'], message: '漁場情報タブの漁場調査日に、調査航海期間外の日付があります！' });
      }
      const hasLatLon = e.latDeg != null && e.latMin != null && e.lonDeg != null && e.lonMin != null;
      if (!hasLatLon && e.zoneNo == null) {
        ctx.addIssue({ code: 'custom', path: ['entries', i, 'zoneNo'], message: '農林漁区番号または緯度経度のどちらかを登録して下さい！' });
      }
    });
  });

export type FishingGroundFormValues = z.infer<typeof fishingGroundFormSchema>;

export function emptyEntry(surveyDate = ''): FishingGroundEntry {
  return {
    surveyDate,
    landingPort: null,
    zoneNo: null,
    latDeg: null,
    latMin: null,
    latSec: null,
    lonDeg: null,
    lonMin: null,
    lonSec: null,
    operationCount: null,
    weather: null,
    windDirection: null,
    windForce: null,
    waterTemp: null,
    depth: null,
    current: null,
    sonarReaction: null,
    schoolCondition: null,
    fishSpeciesId: null,
    standardName: null,
    localName: null,
    count: null,
    weight: null,
    note: null,
  };
}

export function emptyForm(year: number, businessOperatorId: number | null): FishingGroundFormValues {
  return {
    fiscalYear: year,
    businessOperatorId,
    vesselId: null,
    ownerName: null,
    fishingMethod: null,
    gear: null,
    gearCount: null,
    voyageFrom: '',
    voyageTo: '',
    reportSubmittedOn: null,
    reporterName: null,
    landingDate: null,
    fishingGround: null,
    landingTotalInclTax: null,
    shippingDeductionInclTax: null,
    netAmountExclTax: null,
    entries: [],
  };
}

export function toForm(d: FishingGroundDetail): FishingGroundFormValues {
  const { entries, ...h } = d;
  return {
    fiscalYear: h.fiscalYear,
    businessOperatorId: h.businessOperatorId,
    vesselId: h.vesselId,
    ownerName: h.ownerName,
    fishingMethod: h.fishingMethod,
    gear: h.gear,
    gearCount: h.gearCount,
    voyageFrom: h.voyageFrom,
    voyageTo: h.voyageTo,
    reportSubmittedOn: h.reportSubmittedOn,
    reporterName: h.reporterName,
    landingDate: h.landingDate,
    fishingGround: h.fishingGround,
    landingTotalInclTax: h.landingTotalInclTax,
    shippingDeductionInclTax: h.shippingDeductionInclTax,
    netAmountExclTax: h.netAmountExclTax,
    entries: entries.map(({ id: _id, zoneNoCalculated: _z, ...e }) => e),
  };
}

const blankToNull = (v: string | null | undefined) => (v == null || v.trim() === '' ? null : v.trim());

/** フォーム値 → API 入力（空文字を null に） */
export function toInput(v: FishingGroundFormValues): FishingGroundInput {
  return {
    ...v,
    ownerName: blankToNull(v.ownerName),
    fishingMethod: blankToNull(v.fishingMethod),
    gear: blankToNull(v.gear),
    reportSubmittedOn: blankToNull(v.reportSubmittedOn),
    reporterName: blankToNull(v.reporterName),
    landingDate: blankToNull(v.landingDate),
    fishingGround: blankToNull(v.fishingGround),
    entries: v.entries.map((e) => ({
      ...e,
      landingPort: blankToNull(e.landingPort),
      weather: blankToNull(e.weather),
      windDirection: blankToNull(e.windDirection),
      windForce: blankToNull(e.windForce),
      current: blankToNull(e.current),
      schoolCondition: blankToNull(e.schoolCondition),
      standardName: blankToNull(e.standardName),
      localName: blankToNull(e.localName),
      note: blankToNull(e.note),
    })),
  };
}

const norm = (v: string | null | undefined) => (v ?? '').normalize('NFKC').trim();

/**
 * 標準和名 ⇔ 地方名の連動（R08.10.08 検証指摘「和名を入れても地方名が連動しない。またその逆も」）。
 * 入力した名称が魚種マスタの標準和名（または地方名）と一致し、該当が 1 件に決まる場合にその魚種を返す。
 * 複数件該当（同名の地方名が別魚種にある等）・該当なしは null（自動補完しない）。
 */
export function findSpeciesByName(species: readonly FishSpeciesDto[], field: 'standardName' | 'localName', text: string | null | undefined): FishSpeciesDto | null {
  const t = norm(text);
  if (!t) return null;
  const hits = species.filter((s) => norm(s[field]) === t);
  return hits.length === 1 ? hits[0]! : null;
}
